import {
    RotateCcw,
    Fingerprint,
    Split,
    Shuffle,
    Gauge,
    PanelsTopLeft,
} from "lucide-react";
import { SectionHeading, Reveal } from "./primitives";

const CAPS = [
    {
        icon: RotateCcw,
        title: "Automatic retries",
        copy: "Exponential backoff from 1s to 1h across a 24h window, with jitter to dodge thundering herds. Failed events park for one-click replay.",
        span: "sm:col-span-2",
        extra: (
            <div className="mt-5 flex items-end gap-1.5" aria-hidden="true">
                {[8, 14, 22, 34, 52, 74].map((h, i) => (
                    <div
                        key={i}
                        style={{ height: `${h}px` }}
                        className="w-8 rounded-sm border border-cyan-500/20 bg-gradient-to-t from-cyan-950 to-cyan-500/40"
                    />
                ))}
                <span className="ml-3 font-mono text-[10px] text-slate-500">
                    backoff · 1s → 5m → 1h
                </span>
            </div>
        ),
    },
    {
        icon: Fingerprint,
        title: "Idempotency engine",
        copy: "Content-addressed event IDs deduplicate redeliveries before they reach your handler.",
    },
    {
        icon: Split,
        title: "Fanout streaming",
        copy: "One publish, thousands of subscribers. Filter per-endpoint at the edge.",
    },
    {
        icon: Shuffle,
        title: "Payload transforms",
        copy: "Reshape, redact, and version payloads in flight — per destination, no redeploys.",
    },
    {
        icon: Gauge,
        title: "Rate limiting",
        copy: "Per-endpoint throttles protect fragile consumers. Burst credits absorb your launch day.",
    },
    {
        icon: PanelsTopLeft,
        title: "Customer portals",
        copy: "Embedded, white-labeled endpoint management for your own customers — isolated per tenant.",
        span: "sm:col-span-2",
    },
];

export default function Capabilities() {
    return (
        <section
            id="capabilities"
            data-testid="capabilities-section"
            className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-32"
        >
            <SectionHeading
                testid="capabilities"
                eyebrow="Capabilities"
                title="Everything between publish and delivered."
                copy="The unglamorous machinery of event delivery — retries, idempotency, fanout, transforms, throttles — engineered once, so you never build it again."
            />
            <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {CAPS.map((c, i) => (
                    <Reveal
                        key={c.title}
                        delay={(i % 4) * 0.07}
                        className={`group relative overflow-hidden rounded-xl border border-slate-800/80 bg-[#111827] p-6 transition-all duration-300 hover:border-cyan-500/30 hover:bg-[#161F2E] ${c.span || ""}`}
                        data-testid={`capability-card-${c.title.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                        <div className="absolute -right-10 -top-10 h-28 w-28 rounded-full bg-cyan-500/0 blur-2xl transition-all duration-500 group-hover:bg-cyan-500/10" />
                        <c.icon className="h-5 w-5 text-cyan-400" />
                        <h3 className="font-display mt-4 text-base font-bold tracking-tight text-slate-100 sm:text-lg">
                            {c.title}
                        </h3>
                        <p className="mt-2 text-xs leading-relaxed text-slate-400 sm:text-sm">
                            {c.copy}
                        </p>
                        {c.extra}
                    </Reveal>
                ))}
            </div>
        </section>
    );
}
